import React, { useState, useEffect } from 'react'
import { createUseStyles } from '../helpers/createStyles'
import { theme } from '../styles/theme'
import Carousel from './Carousel'
import ResponsiveImage from './ResponsiveImage'
import RichContent from './RichContent'
import Card from './Card'
import cn from 'classnames'
import Arrow from './Arrow'

const DetailPicker = ({ items, defaultItem, hideDetailCard, className, showMobilePreview = true, hidePagination = false, isTep = false }) => {
  const classes = useStyles()
  const [selectedItem, setSelectedItem] = useState(null)
  const [currentPage, setCurrentPage] = useState(0)
  const itemsPerPage = 15 // 3 rows x 5 columns
  const [isMobile, setIsMobile] = useState(window.innerWidth < theme.breakpoints.values.md)
  const [allItems, setAllItems] = useState(items)
  const [paginatedItems, setPaginatedItems] = useState(items.slice(0, itemsPerPage))
  const [detailSubtitle, setDetailSubtitle] = useState('')
  const [detailDescription, setDetailDescription] = useState('')
  const [detailFooterTitle, setDetailFooterTitle] = useState('')
  const [detailFooterDescription, setDetailFooterDescription] = useState('')
  const [detailFooterAddendum, setDetailFooterAddendum] = useState('')

  useEffect(() => {
    if (selectedItem) {
      setDetailSubtitle(defaultItem.selectedSubtitle)
      setDetailDescription(`${selectedItem.name}`)
      setDetailFooterTitle(defaultItem.selectedFooterTitle)
      setDetailFooterDescription(selectedItem.characteristics || selectedItem.features || selectedItem.description)
      setDetailFooterAddendum(null)
    } else {
      setDetailSubtitle(defaultItem.subtitle)
      setDetailDescription(defaultItem.description)
      setDetailFooterTitle(defaultItem.footerTitle)
      setDetailFooterDescription(defaultItem.footerDescription)
      setDetailFooterAddendum(defaultItem.addendum)
    }
  }, [selectedItem])

  useEffect(() => {
    setAllItems(items)
    setPaginatedItems(items.slice(0, itemsPerPage))
  }, [items])

  const handleResize = () => {
    setIsMobile(window.innerWidth < theme.breakpoints.values.md)
  }
  window.addEventListener('resize', handleResize)

  useEffect(() => {
    setPaginatedItems(items.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage))
  }, [currentPage, items])

  const handleHover = (item) => {
    setSelectedItem(item)
  }

  const handlePageChange = (direction) => {
    if (direction === 'next' && (currentPage + 1) * itemsPerPage < items.length) {
      setCurrentPage(currentPage + 1)
    } else if (direction === 'prev' && currentPage > 0) {
      setCurrentPage(currentPage - 1)
    }
  }

  const renderItemContent = (item) => {
    const hasBoth = item.hexCode && item.image

    if (hasBoth) {
      return (
        <div className={classes.colorBlockWrapper}>
          <div
            className={cn(classes.colorBlock, 'colorBlock')}
            style={{ backgroundColor: item.hexCode }}
          />
          <ResponsiveImage
            image={item.image}
            className={cn(classes.itemImage, 'itemImage')}
          />
        </div>
      )
    }

    if (item.hexCode) {
      return (
        <div
          className={classes.colorBlock}
          style={{ backgroundColor: item.hexCode }}
        />
      )
    }

    if (item.image) {
      return <ResponsiveImage image={item.image} className={classes.itemImage} />
    }

    return null
  }

  if (isMobile) {
    return (
      <Carousel itemsLabel={defaultItem?.itemName?.toUpperCase() || 'ITEM'} hideFooters showMobilePreview={showMobilePreview}>

        {/* <pre>
          {typeof items}
          ---
          {`${Array.isArray(items)}`}
        </pre>

                <Carousel>
                  {Array.isArray(tepProducts) && tepProducts.map((product, index) => (
                    <Card key={index} cardContent={product} />
                  ))}
                </Carousel>

        <pre>
          {JSON.stringify(items, null, 2)}
        </pre> */}

        {Array.isArray(allItems) && allItems.map((item, index) => (
          <Card preferHexCode isMobile={isMobile} key={index} cardContent={item} hideFooter={!isTep} />
        ))}
      </Carousel>
    )
  }

  return (
    <div className={cn(classes.detailPicker, className)}>
      {/* Detail Panel */}
      {!hideDetailCard && (
        <div className={`${classes.detailPanel} ${selectedItem && classes.darkBackground}`}>
          <div className={classes.detailPanelCard}>
            {selectedItem ? (
              <div className={`${classes.contentWrapper} ${classes.fadeIn}`} key="selected">
                <RichContent
                  content={detailSubtitle}
                  className={classes.detailSubtitle}
                />
                <div className={classes.centerDescription}>
                  <RichContent
                    content={detailDescription}
                    className={`${classes.detailDescription} ${selectedItem && classes.font100}`}
                  />
                </div>
              </div>
            ) : (
              <div className={`${classes.contentWrapper} ${classes.fadeIn}`} key="default">
                <RichContent
                  content={detailSubtitle}
                  className={classes.detailSubtitle}
                />
                <RichContent
                  content={detailDescription}
                  className={`${classes.detailDescription} ${selectedItem && classes.font100}`}
                />
              </div>
            )}
            <div className={classes.detailPanelCardFooter}>
              <div className={classes.detailFooterDescriptionContainer}>
                <RichContent content={detailFooterTitle} className={classes.detailFooterTitle} />
                <RichContent content={detailFooterDescription} className={classes.detailFooterDescription} />
              </div>
              <div className={classes.detailFooterDescriptionContainer}>
                <div />
                <div className={classes.detailFooterAddendum}><p>{detailFooterAddendum}</p></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* List Panel */}
      <div className={classes.listPanel}>
        <div className={classes.grid}>
          {paginatedItems.map((item, index) => (
            <div
              key={index}
              className={cn(classes.gridItem, item.hexCode && classes.setAspectRatio)}
              onMouseEnter={() => handleHover(item)}
              onMouseLeave={() => handleHover(null)}
            >
              {renderItemContent(item)}
            </div>
          ))}

          {/* make it so if the last page has less than 15, fill the remaining spots with empty div elements so it prevents the UI from bouncing around. */}
          {Array.from({ length: itemsPerPage - paginatedItems.length }).map((_, index) => (
            <div key={`empty-${index}`} className={classes.emptyGridItem} />
          ))}
        </div>
        {(!hidePagination && items?.length > 4) &&
          <div className={classes.pagination}>
            <div>
              <Arrow direction='left' heightSM={12} heightMD={24} onClick={() => handlePageChange('prev')} isDisabled={currentPage === 0} />
              <span className={classes.paginationText}>
                {`${defaultItem.itemName.toUpperCase()} ${String(currentPage * itemsPerPage + 1).padStart(2, '0')} - ${String(Math.min((currentPage + 1) * itemsPerPage, items.length)).padStart(2, '0')} OF ${items.length}`}
              </span>
              <Arrow direction='right' heightSM={12} heightMD={24} onClick={() => handlePageChange('next')} isDisabled={(currentPage + 1) * itemsPerPage >= items.length} />
            </div>
          </div>}
      </div>
    </div>
  )
}

const useStyles = createUseStyles({
  detailPicker: {
    display: 'flex',
    gap: theme.spacing(4),
    overflow: 'hidden',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row'
    }
  },
  detailPanel: {
    flex: 1,
    padding: theme.spacing(8),
    transition: 'background-color 1.2s',
    borderRight: `1px solid ${theme.colors.black}`
  },
  detailPanelCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'all 1.2s',
    height: '100%'
  },
  detailSubtitle: {
    fontFamily: 'Sohne',
    fontWeight: 400,
    fontSize: 12,
    letterSpacing: 1,
    color: theme.colors.black,
    opacity: 0.6,
    '& *': {
      fontFamily: 'Sohne',
      fontWeight: 400,
      fontSize: 12,
      letterSpacing: 1,
      color: theme.colors.black,
      opacity: 0.6
    }
  },
  detailDescription: {
    fontFamily: 'Iskry',
    fontWeight: 700,
    fontSize: 50,
    color: theme.colors.black,
    '& *': {
      fontFamily: 'Iskry',
      fontWeight: 700,
      fontSize: 50,
      color: theme.colors.black
    }
  },
  font100: {
    fontSize: 100,
    '& *': {
      // fontSize: 100,
    }
  },
  detailFooterTitle: {
    fontFamily: 'Sohne',
    fontWeight: 400,
    fontSize: 14,
    color: theme.colors.black,
    opacity: 0.8,
    '& *': {
      fontFamily: 'Sohne',
      fontWeight: 400,
      fontSize: 14,
      color: theme.colors.black,
      opacity: 0.8
    }
  },
  detailFooterDescription: {
    fontFamily: 'Sohne',
    fontWeight: 300,
    fontSize: 14,
    color: theme.colors.black,
    opacity: 0.8,
    '& *': {
      fontFamily: 'Sohne',
      fontWeight: 300,
      fontSize: 14,
      color: theme.colors.black,
      opacity: 0.8
    }
  },
  detailFooterDescriptionContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    justifyContent: 'space-between',

    '& :nth-child(odd)': {
      flex: 1
    },
    '& :nth-child(even)': {
      flex: 2
    }
  },
  detailFooterAddendum: {
    fontFamily: 'Sohne',
    fontWeight: 400,
    fontSize: 14,
    color: theme.colors.black,
    opacity: 0.5,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0),
    '& *': {
      fontFamily: 'Sohne',
      fontWeight: 400,
      fontSize: 14,
      color: theme.colors.black,
      opacity: 0.5,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(0)
    }
  },

  darkBackground: {
    backgroundColor: '#C7C4B4',
    borderRight: `0px solid ${theme.colors.black} !important`
  },

  listPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(8),
    paddingRight: theme.spacing(0),
    gap: theme.spacing(2)
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gridTemplateRows: 'repeat(3, auto)',
    gap: theme.spacing(2)
  },
  emptyGridItem: {
    backgroundColor: 'transparent',
    aspectRatio: '155 / 175',
    border: 'none',
    boxShadow: 'none',
    cursor: 'default',
    '&:hover': {
      border: 'none'
    }

  },
  gridItem: {
    // padding: theme.spacing(2),
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border 1.2s',
    '&:hover': {
      border: '8px solid #AFAC9C'
    }
  },
  setAspectRatio: {
    aspectRatio: '155 / 175'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& div': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: theme.spacing(1.5),

      fontFamily: 'Sohne',
      fontWeight: 400,
      fontSize: 10,
      letterSpacing: 1,
      opacity: 0.6

    },
    '& button': {
      padding: theme.spacing(1, 2),
      border: 'none',
      transition: 'color 1.2s',
      color: theme.colors.black,
      cursor: 'pointer',
      '&:disabled': {
        color: 'lightgray',
        cursor: 'not-allowed'
      }
    }
  },
  carouselItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(4),
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  itemDetail: {
    '& h3': {
      fontSize: 20,
      marginBottom: theme.spacing(2)
    },
    '& p': {
      fontSize: 14
    }
  },
  itemImage: {
    width: '100%',
    height: '100%',
    transition: 'opacity 1.2s ease-out'
    // borderRadius: 8,
    // marginBottom: theme.spacing(2)
  },
  colorBlock: {
    width: '100%',
    height: '100%',
    transition: 'opacity 1.2s ease-out'
  },

  colorBlockWrapper: {
    position: 'relative',
    width: '100%',
    height: '100%',
    cursor: 'pointer',

    '& .itemImage': {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      width: '100%',
      height: '100%',
      aspectRatio: 'auto',
      overflow: 'hidden',
      opacity: 0,
      transition: 'all 1.2s'
    },
    '& .colorBlock': {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      width: '100%',
      height: '100%',
      transition: 'all 1.2s',
      opacity: 1
    },

    '&:hover .itemImage': {
      opacity: 1
    },
    '&:hover .colorBlock': {
      opacity: 0
    }

  },
  fadeIn: {
    animation: 'fadeIn 1.2s ease-in-out',
    '@keyframes fadeIn': {
      '0%': {
        opacity: 0
      },
      '100%': {
        opacity: 1
      }
    }
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1
  },
  centerDescription: {
    display: 'flex',
    flex: 1,
    alignItems: 'center'
  }

})

export default DetailPicker
